import React, { useEffect, useState, useCallback, useMemo } from "react";
import { BellRing, Loader2, Trash2, Check, MessageCircle, Phone } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function RemindersPanel() {
  const [items, setItems] = useState([]);
  const [upcoming, setUpcoming] = useState(0);
  const [loading, setLoading] = useState(true);
  const [onlyPending, setOnlyPending] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/reminders${onlyPending ? "?pending=true" : ""}`);
      setItems(r.data.items || []);
      setUpcoming(r.data.upcoming || 0);
    } catch { toast.error("Could not load reminders"); }
    finally { setLoading(false); }
  }, [onlyPending]);

  useEffect(() => { load(); }, [load]);

  const markNotified = async (id, notified) => {
    try {
      await api.patch(`/admin/reminders/${id}`, { notified });
      toast.success(notified ? "Marked as reminded" : "Moved back to pending");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/reminders/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((r) => {
      const key = `${r.festival_date}|${r.festival_name}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return [...map.entries()];
  }, [items]);

  return (
    <div data-testid="reminders-panel">
      <div className="glass rounded-2xl p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-tara-gold to-tara-vermilion">
            <BellRing className="h-5 w-5 text-white" />
          </span>
          <div>
            <p className="font-serif text-2xl font-bold text-tara-ink leading-none" data-testid="reminders-upcoming-count">{upcoming}</p>
            <p className="text-xs text-tara-muted uppercase tracking-[0.16em] mt-1">Pending reminders ahead</p>
          </div>
        </div>
        <label className="flex items-center gap-2.5 text-sm text-tara-body cursor-pointer">
          <input
            type="checkbox"
            checked={onlyPending}
            onChange={(e) => setOnlyPending(e.target.checked)}
            className="h-4 w-4 accent-[#B4232A]"
            data-testid="reminders-pending-toggle"
          />
          Show only pending
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-tara-gold animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center text-tara-muted" data-testid="reminders-empty">
          <BellRing className="h-10 w-10 mx-auto mb-3 text-tara-muted/70" />
          No reminder requests yet. Devotees can ask for one from any date on the festival calendar.
        </div>
      ) : (
        <div className="space-y-10" data-testid="reminders-list">
          {grouped.map(([key, list]) => {
            const [date, fname] = key.split("|");
            return (
              <section key={key} data-testid={`reminder-group-${date}`}>
                <div className="flex items-center gap-4 mb-4">
                  <h3 className="font-serif text-lg font-semibold text-tara-sindoor whitespace-nowrap">
                    {fname || "Observance"} · {new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                  </h3>
                  <span className="divider-om flex-1" />
                  <span className="text-xs text-tara-muted">{list.length}</span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {list.map((r) => (
                    <div key={r.id} className="glass rounded-2xl p-4 flex items-start justify-between gap-3" data-testid={`reminder-${r.id}`}>
                      <div className="min-w-0">
                        <p className="font-display text-lg text-tara-ink leading-tight">{r.name}</p>
                        <p className="flex items-center gap-1.5 text-xs text-tara-muted mt-1">
                          <Phone className="h-3 w-3" /> {r.phone}
                          <span className="rounded-full bg-white/70 border border-tara-gold/20 px-2 py-0.5 uppercase">{r.language}</span>
                        </p>
                        {r.notified && (
                          <span className="mt-2 inline-block rounded-full border border-green-300 bg-green-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-green-800">
                            reminded
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <a
                          href={`https://wa.me/${r.phone.replace(/\D/g, "")}?text=${encodeURIComponent(
                            `🙏 ${r.name}, this is a reminder from TarapithTantrik.com — ${r.festival_name} falls on ${r.festival_date}. Would you like us to arrange your sankalp puja?`
                          )}`}
                          target="_blank" rel="noopener noreferrer"
                          className="rounded-full border border-green-500/40 p-2 text-green-600 hover:bg-green-500/10 transition"
                          data-testid={`reminder-whatsapp-${r.id}`}
                          aria-label="Send WhatsApp reminder"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </a>
                        <button
                          onClick={() => markNotified(r.id, !r.notified)}
                          className="rounded-full border border-tara-gold/30 p-2 text-tara-goldDeep hover:bg-tara-gold/10 transition"
                          data-testid={`reminder-toggle-${r.id}`}
                          aria-label="Toggle reminded"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => remove(r.id)}
                          className="rounded-full border border-red-500/40 p-2 text-red-600 hover:bg-red-500/10 transition"
                          data-testid={`reminder-delete-${r.id}`}
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
